/**
 * UI helpers for /api/me and member sport-context payloads.
 */

import { sportHasBeltRank } from "./beltSystemCatalog";

/**
 * @param {{ validationBeltSystemKey?: string } | null | undefined} sportBlock
 */
export function validationBeltSystemKey(sportBlock) {
  return sportBlock?.validationBeltSystemKey || "bjj";
}

const NO_BELT_UI_SOURCES = new Set([
  "parent_only",
  "not_student",
  "groups_no_sport_config",
  "groups_no_belt_rank",
]);

/** True when any resolved training group uses belt rank (e.g. BJJ + boxing → true). */
export function memberHasAnyBeltRankSport(sport) {
  if (!sport) return false;
  const sportKeys = sport.sportKeys || [];
  if (sportKeys.some((key) => sportHasBeltRank(key))) return true;
  const beltSystemKeys = sport.beltSystemKeys || [];
  return beltSystemKeys.some((key) => sportHasBeltRank(key));
}

/**
 * Whether member-facing belt UI (nav, profile bar, membership card) should appear.
 * Visible if the member is in any belt-rank group; hidden only when all groups are non-belt.
 * @param {{ sportKeys?: string[]; beltSystemKeys?: string[]; source?: string } | null | undefined} sport — from /api/me
 * @param {{ loaded?: boolean }} [options]
 */
export function memberShowsBeltRankMenu(sport, { loaded = true } = {}) {
  if (!loaded) return false;
  if (!sport) return false;
  if (NO_BELT_UI_SOURCES.has(sport.source)) {
    return false;
  }
  return memberHasAnyBeltRankSport(sport);
}

/**
 * Admin read-only summary for group dialog.
 * @param {{ sportKeys?: string[]; beltSystemKeys?: string[]; effectiveBeltSystemKey?: string|null; validationBeltSystemKey?: string; beltRankConflict?: boolean; source?: string } | null | undefined} sport
 * @param {(key: string) => string} t
 */
export function memberSportAdminSummaryLines(sport, t) {
  if (!sport) {
    return [t("member_sport_context_unavailable")];
  }
  if (sport.source === "parent_only") {
    return [t("member_sport_context_parent_only")];
  }
  if (sport.source === "not_student") {
    return [t("member_sport_context_not_student")];
  }

  const lines = [];
  const activities = sport.sportKeys || [];
  if (activities.length) {
    lines.push(
      t("member_sport_context_activities", {
        list: activities.map((k) => t(`sport_key_${k}`, k)).join(", "),
      })
    );
  } else {
    lines.push(t("member_sport_context_activities_legacy"));
  }

  const belts = sport.beltSystemKeys || [];
  if (belts.length) {
    lines.push(
      t("member_sport_context_belt_rank", {
        list: belts.map((k) => t(`sport_key_${k}`, k)).join(", "),
      })
    );
  } else {
    lines.push(t("member_sport_context_belt_legacy"));
  }

  if (sport.beltRankConflict) {
    lines.push(t("member_sport_context_belt_conflict"));
  }

  return lines;
}
